import React, { useEffect, useRef, useState, useMemo } from "react"
import Flickity from "flickity"

import { chevronLeft, chevronRight } from "../hooks/icons"
import { isMobile } from "../lib/utils"
import { useSearchspringRecommendResults } from "@app/scripts/hooks/use-searchspring-recommend-results"
import { CardProduct } from "./card-product"
import { useSwatchCache } from "../hooks/use-swatches"
import { currentCountryCurrency } from "@app/scripts/lib/utils"
import {
  useShopifyPrice,
  mergeResultsWithPricing,
} from "@app/scripts/hooks/use-shopify"
export type Product = {
  id: string
  title: string
}

type Props = {
  visible: boolean
  position?: "relative" | "absolute"
  carouselName?: string
  setCarouselData?: React.Dispatch<React.SetStateAction<RvsiItem[]>>
}

export function ProductCarousel({
  visible,
  position = "relative",
  carouselName,
  setCarouselData,
}: Props) {
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const carouselRef = useRef<HTMLDivElement>(null)
  const flickityInstance = useRef<Flickity | null>(null)

  const results = useSearchspringRecommendResults()
  const swatchColourData = useSwatchCache(results)

  // Formats data for useShopifyPrice
  const formattedProductData = useMemo(
    () =>
      results.map((result) => ({
        price: result.price || 0,
        compareAtPrice: result.msrp || 0,
        id: result?.attributes?.ss_id || "",
        handle: result?.attributes?.handle || "",
      })),
    [results]
  )

  // Formats data (returns new prices or AUD prices)
  const formattedPriceData = useShopifyPrice(
    formattedProductData,
    currentCountryCurrency
  )

  // Create new products array with updated price data or defaults back to results
  const productsWithUpdatedPrices = useMemo(() => {
    return mergeResultsWithPricing(results, formattedPriceData)
  }, [results, formattedPriceData])

  useEffect(() => {
    if (setCarouselData && carouselName) {
      setCarouselData((prevCarouselData) => {
        const newCarouselData = prevCarouselData.map((carousel) =>
          carousel.profileTag === carouselName
            ? { ...carousel, count: results.length }
            : carousel
        )
        return newCarouselData
      })
    }
  }, [results, carouselName, setCarouselData])

  useEffect(() => {
    if (!carouselRef.current || flickityInstance.current || !results?.length)
      return

    setTimeout(() => {
      flickityInstance.current = new Flickity(carouselRef.current, {
        cellAlign: "left",
        pageDots: false,
        contain: true,
        imagesLoaded: true,
        adaptiveHeight: false,
        prevNextButtons: false,
        draggable: true,
      })

      const flkty = flickityInstance.current

      setCanPrev(!flkty.isBeginning)
      setCanNext(!flkty.isEnd)

      flkty.on("select", () => {
        setCanPrev(!flkty.isBeginning)
        setCanNext(!flkty.isEnd)
      })
    }, 50)

    return () => {
      flickityInstance.current.destroy()
      flickityInstance.current = null
    }
  }, [results])

  const goPrev = () => {
    flickityInstance.current?.previous()
  }

  const goNext = () => {
    flickityInstance.current?.next()
  }

  return (
    <div
      className="product-carousel"
      style={{
        visibility: visible ? "visible" : "hidden",
        position,
        zIndex: visible ? 1 : -1,
        top: 0,
      }}
    >
      {!isMobile && (
        <div className="product-carousel--buttons-container">
          <button
            className="product-carousel--prev-button"
            onClick={goPrev}
            disabled={!canPrev}
          >
            {chevronLeft}
          </button>
          <button
            className="product-carousel--next-button"
            onClick={goNext}
            disabled={!canNext}
          >
            {chevronRight}
          </button>
        </div>
      )}

      <div ref={carouselRef} className="carousel">
        {productsWithUpdatedPrices?.map((product, index) => (
          <div key={index} className="carousel-cell">
            <CardProduct
              data={{ ...product, ...product.attributes }}
              productCarousel={true}
              swatchColours={swatchColourData}
              currentCountryCurrency={currentCountryCurrency}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
